import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SessionRepresentation,
  deleteSession,
  useEnvironment,
} from '@keycloak/keycloak-account-ui';
import {
  ArrowForward,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Popover,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { ReactComponent as Desktop } from '@/icons/desktop-windows.svg';
import { ReactComponent as Mobile } from '@/icons/phone-iphone.svg';

import { lightBorderColor } from './theme';
import { formatDate } from './utils/date';
import { desktopMediaQuery } from './utils/mediaQuery';

type Session = SessionRepresentation & {
  os: string;
  osVersion: string;
  device: string;
  mobile: boolean;
};

export default function DevicesRow({
  session,
  refresh,
}: {
  session: Session;
  refresh: () => void;
}) {
  const { t } = useTranslation('sessions');
  const context = useEnvironment();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const isDesktop = useMediaQuery(desktopMediaQuery);

  const menuIconRef = useRef(null);

  const logoutSession = () => {
    setLoading(true);
    deleteSession(context, session.id).finally(() => {
      setLoading(false);
      refresh();
    });
  };

  const getBrowser = () => {
    const browser = session.browser.toLowerCase();

    if (browser.includes('chrome') || browser.includes('chromium')) {
      return 'chrome';
    }

    if (browser.includes('firefox')) {
      return 'firefox';
    }

    if (browser.includes('safari')) {
      return 'safari';
    }

    return 'chrome';
  };

  const renderSessionCell = () => (
    <TableCell
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start' },
        gap: 1.25,
        height: 72,
        paddingRight: { xs: 0.5, sm: 2 },
      }}
    >
      <img
        style={{ width: 24, height: 24 }}
        src={`${context.environment.resourceUrl}/${getBrowser()}.png`}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: { xs: 112, sm: 'unset' },
          overflow: 'auto',
        }}
      >
        <Typography>{session.browser}</Typography>
        <Typography fontSize={14} color="secondary">
          {session.ipAddress}
        </Typography>
      </Box>
      {isDesktop ? session.mobile ? <Mobile /> : <Desktop /> : null}
      {session.current && (
        <Chip
          label={t('current')}
          variant="outlined"
          color="success"
          sx={{ borderRadius: 2, marginLeft: { xs: 'auto', md: 0.75 } }}
        />
      )}
    </TableCell>
  );

  if (!isDesktop) {
    return (
      <>
        <TableRow
          sx={{
            '& td': { border: 0 },
            backgroundColor: (theme) => theme.palette.background.paper,
            height: 72,
          }}
        >
          {renderSessionCell()}
          <TableCell sx={{ textAlign: 'end', paddingLeft: { xs: 0.5, sm: 2 } }}>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow
          sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  borderBottom: `1px solid ${lightBorderColor})`,
                  marginBottom: 2,
                  marginTop: 3,
                  gap: 2,
                }}
              >
                <div>
                  <Typography fontSize={14} mb={1} color="secondary">
                    {t('table.lastActive')}
                  </Typography>
                  <Typography>
                    {formatDate(session.lastAccess * 1000)}
                  </Typography>
                </div>
                <div>
                  <Typography fontSize={14} mb={1} color="secondary">
                    {t('table.expires')}
                  </Typography>
                  <Typography>{formatDate(session.expires * 1000)}</Typography>
                </div>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    sx={{ textTransform: 'none' }}
                    onClick={logoutSession}
                  >
                    <Typography
                      sx={{ marginRight: 1.25, fontWeight: 700, fontSize: 14 }}
                    >
                      {t('logoutSession')}
                    </Typography>
                    {!isLoading && (
                      <ArrowForward sx={{ width: 18, height: 18 }} />
                    )}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  return (
    <TableRow
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        backgroundColor: (theme) => theme.palette.background.paper,
        height: 72,
      }}
    >
      {renderSessionCell()}
      <TableCell sx={{ fontSize: 16, fontWeight: 500 }}>
        {formatDate(session.lastAccess * 1000)}
      </TableCell>
      <TableCell sx={{ fontSize: 16, fontWeight: 500 }}>
        {formatDate(session.expires * 1000)}
      </TableCell>
      <TableCell align="right">
        <IconButton ref={menuIconRef} onClick={() => setPopoverOpen(true)}>
          <MoreVert />
        </IconButton>
        <Popover
          anchorEl={menuIconRef.current}
          open={popoverOpen}
          onClose={() => setPopoverOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
              },
            },
          }}
        >
          <Button
            sx={{
              padding: '24px 30px',
              height: 72,
              textTransform: 'none',
              paddingRight: 3,
            }}
            onClick={logoutSession}
            loading={isLoading}
            loadingPosition="center"
          >
            <Typography sx={{ marginRight: 1.25 }}>{t('logout')}</Typography>
            {!isLoading && <ArrowForward sx={{ width: 18, height: 18 }} />}
          </Button>
        </Popover>
      </TableCell>
    </TableRow>
  );
}
